import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { howItWorks } from './landingConstants';
import './LandingHowItWorksSection.css';

const LandingHowItWorksSection: React.FC = () => {
  return (
    <section
      id="how-it-works"
      className="landing-how-it-works landing-section-equal py-8 sm:py-10 lg:py-14 bg-slate-950 border-y border-white/5"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center max-w-3xl mx-auto mb-12 sm:mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <span className="inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium bg-cyan-500/15 text-cyan-200 border border-cyan-400/20 mb-3 sm:mb-4">
            How It Works
          </span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-100 mb-4 sm:mb-6">
            Launch campaigns in four clear steps
          </h2>
          <p className="text-sm sm:text-base lg:text-lg text-slate-300">
            Set up your AI agent, upload leads, launch calls, and review actionable intelligence in one workflow.
          </p>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 relative">
          {howItWorks.map((step, index) => (
            <motion.div
              key={step.title}
              className="landing-how-it-works__card relative bg-slate-900/80 rounded-2xl border border-white/10 p-5 sm:p-6 shadow-[0_12px_28px_-18px_rgba(2,6,23,0.9)]"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: index * 0.08 }}
            >
              <div className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-gradient-to-r from-cyan-500 to-sky-500 text-white font-semibold mb-4">
                {index + 1}
              </div>
              <div className="inline-flex items-center justify-center h-10 w-10 rounded-lg bg-cyan-500/15 text-cyan-300 mb-4 ml-3">
                <step.icon className="h-5 w-5" />
              </div>
              <h3 className="text-lg font-semibold text-slate-100 mb-2">{step.title}</h3>
              <p className="text-sm text-slate-300">{step.description}</p>
              {index < howItWorks.length - 1 && (
                <div className="landing-how-it-works__connector hidden lg:block" aria-hidden="true" />
              )}
            </motion.div>
          ))}
        </div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.45, delay: 0.2 }}
          className="mt-10 sm:mt-12 landing-how-it-works__cta"
        >
          <p className="text-sm sm:text-base text-slate-200 text-center mb-4">
            Try all 4 steps today — start with 20 free credits. No credit card required.
          </p>
          <div className="flex justify-center items-center">
            <Link
              to="/signup"
              className="px-4 sm:px-5 py-2.5 rounded-md bg-gradient-to-r from-cyan-500 to-sky-500 hover:from-cyan-400 hover:to-sky-400 text-white text-sm font-medium inline-flex items-center transition-all duration-300 shadow-md shadow-cyan-500/20"
            >
              Get started free
              <ArrowRightIcon className="h-4 w-4 ml-2" />
            </Link>
          </div>
        </motion.div>
        <div className="mt-6 text-center">
          <a href="#" className="landing-back-to-top">
            Back to top
            <span aria-hidden="true">↑</span>
          </a>
        </div>
      </div>
    </section>
  );
};

export default LandingHowItWorksSection;
